import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onIdTokenChanged, type User } from "firebase/auth";
import { auth } from "../lib/firebase";

export type Role = "customer" | "owner" | "manager" | "staff" | "provider" | "admin";

export interface Session {
  user: User | null;
  role: Role;
  orgId: string | null;
  loading: boolean;
}

const AuthContext = createContext<Session>({
  user: null,
  role: "customer",
  orgId: null,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>({
    user: null,
    role: "customer",
    orgId: null,
    loading: true,
  });

  useEffect(() => {
    return onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setSession({ user: null, role: "customer", orgId: null, loading: false });
        return;
      }
      const token = await user.getIdTokenResult();
      setSession({
        user,
        role: (token.claims.role as Role | undefined) ?? "customer",
        orgId: (token.claims.orgId as string | undefined) ?? null,
        loading: false,
      });
    });
  }, []);

  const value = useMemo(() => session, [session]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSession(): Session {
  return useContext(AuthContext);
}
